import { FirebaseAuthentication, ProviderId, type User } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import {
  signInAnonymously as jsSignInAnonymously,
  signOut as jsSignOut,
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
  OAuthProvider,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth as jsAuth, app } from '@/lib/firebase';
import { requestGoogleAuthCode } from '@/lib/googleIdentityServices';
import { callExchangeGoogleCode } from '@/lib/calendarClient';
import { startIosOAuthFlow } from '@/services/IosOAuthService';
import { stampLifecycle, touchLifecycleActivity } from '@/services/lifecycle';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { startAllSync, stopAllSync } from '@/stores/sync';
import { loginRevenueCat, logoutRevenueCat } from '@/lib/revenueCat';
import { deriveJsAuthIdentity, applyJsAuthIdentity } from '@/services/authIdentity';

let cachedUser: User | null = null;
let initPromise: Promise<User> | null = null;

const listeners = new Set<(user: User | null) => void>();

let jsAnonSignInFlight = false;

// Firestore sync lifecycle is driven by the JS SDK's auth state — the identity
// Firestore and httpsCallable actually authenticate with — NOT the native
// plugin's. The native plugin reports the authenticated user a beat before
// signInWithCredential bridges that identity into the JS SDK; starting sync off
// the native state therefore raced ahead of the bridge and the first Firestore
// read went out as the leftover anonymous user → permission-denied (and
// Firestore tears the listener down, so it never recovered until relaunch).
// Keying off the JS SDK guarantees sync only starts once the real uid is live
// where Firestore reads it — correct for fresh sign-in AND cold start (where the
// JS SDK restores the real uid from IndexedDB before this fires).
let syncedUid: string | null = null;
onAuthStateChanged(jsAuth, (u) => {
  // UI identity is authoritative here — the JS SDK restores the real user from
  // IndexedDB on cold start (incl. Apple, which uses skipNativeAuth and so has
  // NO native session to restore). Native authStateChange must not override it.
  const identity = deriveJsAuthIdentity(u, useUIStore.getState().lastAuthProvider);
  applyJsAuthIdentity(identity);

  // lastActiveAt drives the drip's dormant/free-active segmenting. Sessions
  // persist, so the sign-in-time stamp alone froze it at first sign-in —
  // refresh on every cold start (throttled to one write per local day).
  if (identity.authenticated) void touchLifecycleActivity(identity);

  const uid = u && !u.isAnonymous ? u.uid : null;
  if (uid === syncedUid) return;
  if (syncedUid) stopAllSync();
  syncedUid = uid;
  if (uid) startAllSync(uid);
});

// uiStore.lastAuthProvider (the persisted sign-in provider) hydrates
// ASYNCHRONOUSLY on native (Capacitor Preferences). If the cold-start
// onAuthStateChanged above fired before hydration finished, it read a null
// provider and fell back to the unreliable providerData[0] heuristic — which
// mislabels a dual-linked Apple account as Google. Once hydration lands, re-derive
// the identity for the current user so the remembered provider wins. Label-only:
// no sync or RevenueCat re-trigger (those already ran on the first emission).
if (!useUIStore.persist.hasHydrated()) {
  const unsubProviderHydration = useUIStore.persist.onFinishHydration(() => {
    unsubProviderHydration();
    const current = jsAuth.currentUser;
    if (!current || current.isAnonymous) return;
    const identity = deriveJsAuthIdentity(current, useUIStore.getState().lastAuthProvider);
    if (identity.authenticated) {
      useAuthStore.getState().setAuthenticated({
        uid: identity.uid,
        displayName: identity.displayName,
        email: identity.email,
        provider: identity.provider,
      });
    }
  });
}

// A foregrounded app can run for days without a cold start (no auth-state
// emission), so also refresh activity when the WebView becomes visible again —
// same once-per-local-day throttle as above.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  const identity = deriveJsAuthIdentity(jsAuth.currentUser, useUIStore.getState().lastAuthProvider);
  if (identity.authenticated) void touchLifecycleActivity(identity);
});

void FirebaseAuthentication.addListener('authStateChange', ({ user }) => {
  cachedUser = user;
  // Identity + RevenueCat are driven by the JS-SDK handler above (the session
  // that persists across cold start). The native plugin only: caches the user
  // for useAuth(), and bridges an anonymous native user into the JS SDK.
  if (
    Capacitor.isNativePlatform() &&
    user?.isAnonymous &&
    !jsAuth.currentUser &&
    !jsAnonSignInFlight
  ) {
    jsAnonSignInFlight = true;
    // jsAuth.currentUser is transiently null on a true cold start while
    // indexedDBLocalPersistence restores asynchronously. Signing in anonymously
    // in that window CLOBBERS a real user mid-restore — notably Apple, which uses
    // skipNativeAuth and so has ONLY a JS session (no native session to fall back
    // on), which is why force-quitting signed Apple users out. authStateReady()
    // resolves once the initial persisted state is settled; only bridge if the JS
    // SDK is still empty then.
    void jsAuth
      .authStateReady()
      .then(() => (jsAuth.currentUser ? undefined : jsSignInAnonymously(jsAuth)))
      .finally(() => {
        jsAnonSignInFlight = false;
      });
  }
  for (const cb of listeners) cb(user);
});

export const subscribeAuth = (cb: (user: User | null) => void): (() => void) => {
  listeners.add(cb);
  cb(cachedUser);
  return () => listeners.delete(cb);
};

export const getCurrentUser = (): User | null => cachedUser;

export const ensureAuthenticated = async (): Promise<User> => {
  if (cachedUser) return cachedUser;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const { user } = await FirebaseAuthentication.getCurrentUser();
    if (user) {
      cachedUser = user;
      return user;
    }
    const result = await FirebaseAuthentication.signInAnonymously();
    if (!result.user) throw new Error('Anonymous sign-in returned no user');
    cachedUser = result.user;
    return result.user;
  })();

  try {
    return await initPromise;
  } finally {
    initPromise = null;
  }
};

// Narrowest scope that covers our usage: we only ever GET events from the
// primary calendar (functions/src/google/googleCalendarApi.ts). calendar.events.readonly
// reads events without granting calendar-list/settings access — minimizes the
// data we request and lowers Google OAuth verification risk.
const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events.readonly';

export interface GoogleLinkResult {
  accessToken: string;
  refreshToken?: string;
  expiresInSec: number;
  email?: string;
  mintedBy?: 'web' | 'ios';
}

async function linkWithGoogleWeb(): Promise<GoogleLinkResult> {
  const clientId = import.meta.env.VITE_GOOGLE_OAUTH_WEB_CLIENT_ID;
  if (!clientId) {
    throw new Error('VITE_GOOGLE_OAUTH_WEB_CLIENT_ID is not configured');
  }
  // openid + email are required so Google returns an id_token from which the
  // server (exchangeGoogleCode) extracts the user's email for the UI.
  const { code, redirectUri } = await requestGoogleAuthCode({
    clientId,
    scopes: ['openid', 'email', CALENDAR_SCOPE],
  });
  const exchanged = await callExchangeGoogleCode({ code, redirectUri });
  return {
    accessToken: exchanged.accessToken,
    expiresInSec: exchanged.expiresInSec,
    ...(exchanged.email ? { email: exchanged.email } : {}),
  };
}

export async function linkWithGoogle(): Promise<GoogleLinkResult> {
  if (Capacitor.getPlatform() === 'web') {
    return linkWithGoogleWeb();
  }
  const result = await startIosOAuthFlow();
  return {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    expiresInSec: result.expiresInSec,
    ...(result.email ? { email: result.email } : {}),
    mintedBy: 'ios',
  };
}

export async function unlinkGoogle(): Promise<void> {
  await FirebaseAuthentication.unlink({ providerId: ProviderId.GOOGLE });
}

/* ------------------------------------------------------------------ */
/*  Identity sign-in (Firebase Auth user), sign-out, delete           */
/* ------------------------------------------------------------------ */

export interface SignInResult {
  uid: string;
  email: string | null;
  displayName: string | null;
}

/**
 * Sign in with Google as the Firebase Auth identity provider.
 *
 * - **Web:** Uses `signInWithPopup` with the Firebase `GoogleAuthProvider`.
 * - **iOS/Android:** Delegates to the Capacitor Firebase Authentication plugin,
 *   which handles the native Google Sign-In flow and creates the credential
 *   internally. If the native plugin returns an idToken we bridge it into the
 *   JS Auth instance via `signInWithCredential` so Firestore rules see the user.
 */
export async function signInWithGoogle(): Promise<SignInResult> {
  if (Capacitor.getPlatform() === 'web') {
    return signInWithGoogleWeb();
  }
  return signInWithGoogleNative();
}

async function signInWithGoogleWeb(): Promise<SignInResult> {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  const result = await signInWithPopup(jsAuth, provider);
  const { uid, displayName, email } = result.user;
  useAuthStore.getState().setAuthenticated({ uid, displayName, email, provider: 'google' });
  void stampLifecycle({ uid, email, displayName, provider: 'google' });
  useUIStore.getState().setLastAuthProvider('google');
  void loginRevenueCat(uid);
  return { uid, displayName, email };
}

async function signInWithGoogleNative(): Promise<SignInResult> {
  const nativeResult = await FirebaseAuthentication.signInWithGoogle();
  const idToken = nativeResult.credential?.idToken;
  const accessToken = nativeResult.credential?.accessToken;

  // Bridge into JS Auth so Firestore rules see the authenticated user.
  if (idToken) {
    const oauthCredential = GoogleAuthProvider.credential(idToken, accessToken ?? null);
    await signInWithCredential(jsAuth, oauthCredential);
  }

  const user = nativeResult.user;
  if (!user) throw new Error('Google sign-in returned no user');

  const uid = user.uid;
  const displayName = user.displayName ?? null;
  const email = user.email ?? null;
  useAuthStore.getState().setAuthenticated({ uid, displayName, email, provider: 'google' });
  void stampLifecycle({ uid, email, displayName, provider: 'google' });
  useUIStore.getState().setLastAuthProvider('google');
  void loginRevenueCat(uid);
  return { uid, displayName, email };
}

/**
 * Sign in with Apple as the Firebase Auth identity provider.
 *
 * - **iOS/Android:** Delegates to the Capacitor Firebase Authentication plugin.
 *   Bridges the credential into JS Auth via `signInWithCredential`.
 * - **Web:** Uses `signInWithPopup` with an Apple `OAuthProvider`.
 */
export async function signInWithApple(): Promise<SignInResult> {
  if (Capacitor.getPlatform() === 'web') {
    return signInWithAppleWeb();
  }
  return signInWithAppleNative();
}

async function signInWithAppleWeb(): Promise<SignInResult> {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  const result = await signInWithPopup(jsAuth, provider);
  const { uid, displayName, email } = result.user;
  useAuthStore.getState().setAuthenticated({ uid, displayName, email, provider: 'apple' });
  void stampLifecycle({ uid, email, displayName, provider: 'apple' });
  useUIStore.getState().setLastAuthProvider('apple');
  void loginRevenueCat(uid);
  return { uid, displayName, email };
}

async function signInWithAppleNative(): Promise<SignInResult> {
  // skipNativeAuth: true → the plugin runs Apple's native sheet and returns the
  // OAuth credential WITHOUT signing into the native Firebase SDK, so we sign
  // into the JS SDK exactly once. Under skipNativeAuth:false the native SDK
  // consumes Apple's single-use nonce, so re-submitting the same credential to
  // the JS SDK then fails with auth/missing-or-invalid-nonce ("Duplicate
  // credential received"). The signed-in user therefore comes from the JS SDK
  // result, not nativeResult.user (which is absent when skipNativeAuth is true).
  const nativeResult = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
  const idToken = nativeResult.credential?.idToken;
  const nonce = nativeResult.credential?.nonce;
  if (!idToken) throw new Error('Apple sign-in returned no idToken');

  const appleProvider = new OAuthProvider('apple.com');
  const credOpts: { idToken: string; rawNonce?: string } = { idToken };
  if (nonce) credOpts.rawNonce = nonce;
  const oauthCredential = appleProvider.credential(credOpts);
  const { user } = await signInWithCredential(jsAuth, oauthCredential);

  const uid = user.uid;
  const displayName = user.displayName ?? null;
  const email = user.email ?? null;
  useAuthStore.getState().setAuthenticated({ uid, displayName, email, provider: 'apple' });
  void stampLifecycle({ uid, email, displayName, provider: 'apple' });
  useUIStore.getState().setLastAuthProvider('apple');
  void loginRevenueCat(uid);
  return { uid, displayName, email };
}

/**
 * Sign out the current user: stop Firestore syncs, sign out from Firebase,
 * reset the auth store to anonymous, then sign back in anonymously so the
 * app continues to function in offline-first mode.
 */
export async function signOut(): Promise<void> {
  stopAllSync();
  void logoutRevenueCat();
  await FirebaseAuthentication.signOut();
  // FirebaseAuthentication.signOut() clears the NATIVE session only; the JS SDK
  // (which Firestore/callables use) keeps the bridged identity. Sign it out too
  // so the JS-auth sync controller stops and the next anonymous user's WebView
  // doesn't retain the prior real uid.
  await jsSignOut(jsAuth);
  useAuthStore.getState().setAnonymous();
  useUIStore.getState().setLastAuthProvider(null);
  await FirebaseAuthentication.signInAnonymously();
}

/**
 * Delete the user's account via a Cloud Function (which handles server-side
 * cleanup), then sign out locally.
 */
export async function deleteAccount(): Promise<void> {
  const functions = getFunctions(app, 'us-central1');
  const deleteFn = httpsCallable(functions, 'deleteAccount');
  await deleteFn();
  await signOut();
}

/**
 * Returns the current auth mode from the auth store.
 */
export function getAuthMode(): 'anonymous' | 'authenticated' {
  return useAuthStore.getState().authMode;
}
